import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import ReportService from "../services/ReportService";
import { showErrorToast, showSuccessToast } from "../utils/AlertUtils";
import HeadService from "../services/HeadService";
import { useLoading } from "../context/LoadingContext";
import LedgerService from "../services/LedgerService";
import guLocale from "../locales/gu";

const GenerateReport = () => {
  const locale = guLocale.generateReport;
  const [reportType, setReportType] = useState("");
  const [headId, setHeadId] = useState("");
  const { setIsLoading } = useLoading();
  const [heads, setHeads] = useState([]);
  const [totalGrant, setTotalGrant] = useState(0);
  const [usedGrant, setUsedGrant] = useState(0);
  const [remainingGrant, setRemainingGrant] = useState(0);

  const reports = [
    { value: "cashbook", label: locale.reportTypeCashbook },
    { value: "appendix9", label: locale.reportTypeAppendix9 },
    { value: "appendix10", label: locale.reportTypeAppendix10 },
    { value: "khatavahi", label: locale.reportTypeKhatavahi },
    { value: "billregister", label: locale.reportTypeBillRegister },
    { value: "grantregister", label: locale.reportTypeGrantRegister },
    { value: "chequeregister", label: locale.reportTypeChequeRegister },
  ];

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [headsResponse, ledgerResponse] = await Promise.all([
        HeadService.getHeads(),
        LedgerService.ledgerCalculations(),
      ]);

      if(headsResponse && headsResponse.statusCode === 200){
        setHeads(headsResponse?.data);
      }
      if (ledgerResponse?.statusCode === 200) {
        setTotalGrant(ledgerResponse?.data?.totalGrant || 0);
        setUsedGrant(ledgerResponse?.data?.usedGrant || 0);
        setRemainingGrant(ledgerResponse?.data?.remainingGrant || 0);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
      fetchData();
  }, [fetchData]);

  const handleReportChange = (event) => {
    setReportType(event.target.value);
  };

  const handleHeadIdChange = (event) => {
    setHeadId(event.target.value);
  };

  const handleDownload = async () => {
    if (!reportType) {
      showErrorToast(locale.pleaseSelectReportType);
      return;
    }
    if (reportType === "khatavahi" && !headId) {
      showErrorToast(locale.pleaseEnterHeadId);
      return;
    }
    setIsLoading(true);
    try {
      const response = await ReportService.generateReport(reportType, headId);
    if (response) {
      setHeadId("");
      showSuccessToast(`અહેવાલ સફળતાપૂર્વક જનરેટ થયો.`);
    }
    } catch (error) {
      console.log(error);
      const errorMsg = `અહેવાલ જનરેટ થયો નથી. કૃપા કરીને ફરીથી જનરેટ કરો`;
      showErrorToast(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const isGenerateButtonDisabled = () => {
    if (!reportType) return true;
    if (reportType === "khatavahi" && !headId) return true;
    return false;
  };

  const pdfClass = reportType === "khatavahi" ? "mt-3" : "";

  return (
    <div>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">{locale.reports}</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/">{locale.home}</a>
                  </li>
                  <li className="breadcrumb-item active">{locale.reports}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="custom-callout custom-callout-warning">
                  <i className="fas fa-info-circle" style={{ fontSize: '1.3em' }}></i> <span className="mr-1">{locale.note}</span>{locale.noteText}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{locale.downloadReports}</h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* New Design for card */}
                      <div className="col-12 col-sm-4">
                        <div className="small-box bg-success">
                          <div className="inner">
                            <h3>
                              {totalGrant}
                              <sup style={{ fontSize: 20 }}></sup>
                            </h3>
                            <p>{locale.totalGrants}</p>
                          </div>
                          <div className="icon">
                            <i className="ion ion-android-arrow-down custom-ion-icon"/>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-sm-4">
                        <div className="small-box bg-danger">
                          <div className="inner">
                            <h3>{usedGrant}</h3>
                            <p>{locale.totalUsedGrants}</p>
                          </div>
                          <div className="icon">
                            <i className="ion ion-android-arrow-up custom-ion-icon"/>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-sm-4">
                        <div className="small-box bg-info">
                          <div className="inner">
                            <h3>{remainingGrant}</h3>
                            <p>{locale.totalRemainingGrants}</p>
                          </div>
                          <div className="icon">
                            <i className="ion ion-ios-calculator custom-ion-icon"/>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <h4>{locale.reports}</h4>
                        <Form onSubmit={handleDownload}>
                          <Form.Group className="mb-3" controlId="reportType">
                            <Form.Control
                              as="select"
                              value={reportType}
                              onChange={handleReportChange}
                            >
                              <option value="">{locale.selectReportType}</option>
                              {reports.map((report) => (
                                <option key={report.value} value={report.value}>
                                  {report.label}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Form>
                        {reportType === "khatavahi" && (
                          <Form.Group controlId="headId">
                            <Form.Label>{locale.selectHead}</Form.Label>
                            <Form.Control
                              as="select"
                              value={headId}
                              onChange={handleHeadIdChange}
                            >
                              <option value="">{locale.selectHead}</option>
                              {heads.map((head) => (
                                <option key={head._id} value={head._id}>
                                  {head.head_name}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        )}
                      </div>
                      <div className={`col-12 ${pdfClass}`}>
                        <button
                          type="button"
                          className="btn btn-primary float-left"
                          style={{ marginRight: 5 }}
                          disabled={isGenerateButtonDisabled()}
                          onClick={handleDownload}
                        >
                          <i className="fas fa-download" /> {locale.generatePDF}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GenerateReport;
